/*
  TheShapebox : comportements de l'en-tête custom.
  - Menus déroulants de la navigation (éléments details).
  - Compteur du panier, tenu à jour avec les événements standard de Shopify.
  - Hauteur de l'en-tête exposée aux composants d'Horizon (--header-height).
  - Tiroir du panier : ouvert une seule fois après un ajout, pas rouvert sur les pages suivantes.
  Tout passe par délégation d'événements pour continuer de fonctionner
  quand l'éditeur de thème recharge une section.
*/

const NAV_DETAILS = 'details[data-tsb-nav-details]';

/**
 * Ferme les menus déroulants ouverts.
 * @param {Element | null} [except] - Menu à laisser ouvert.
 */
function closeNavDetails(except = null) {
  for (const details of document.querySelectorAll(`${NAV_DETAILS}[open]`)) {
    if (details !== except) details.removeAttribute('open');
  }
}

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (details instanceof HTMLDetailsElement && details.matches(NAV_DETAILS) && details.open) {
      closeNavDetails(details);
    }
  },
  true
);

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  closeNavDetails(target.closest(NAV_DETAILS));
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const open = document.querySelector(`${NAV_DETAILS}[open]`);
  if (!open) return;
  open.removeAttribute('open');
  open.querySelector('summary')?.focus();
});

document.addEventListener('focusout', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const details = target.closest(NAV_DETAILS);
  if (!details) return;
  const next = event.relatedTarget;
  if (next instanceof Node && !details.contains(next)) details.removeAttribute('open');
});

/**
 * Met à jour tous les compteurs du panier de l'en-tête.
 * @param {number} count - Nombre d'articles dans le panier.
 */
function setCartCount(count) {
  const value = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;

  for (const element of document.querySelectorAll('[data-tsb-cart-count]')) {
    element.textContent = String(value);
  }
  for (const element of document.querySelectorAll('[data-tsb-cart-dot]')) {
    if (element instanceof HTMLElement) element.hidden = value === 0;
  }
  for (const element of document.querySelectorAll('[data-tsb-cart-empty]')) {
    if (element instanceof HTMLElement) element.dataset.tsbCartEmpty = value === 0 ? 'true' : 'false';
  }
}

import('@shopify/events')
  .then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise
        ?.then(({ cart, detail }) => setCartCount(cart?.totalQuantity ?? detail?.itemCount ?? 0))
        .catch(() => {});
    });
  })
  .catch(() => {});

// Retour arrière du navigateur : la page vient du cache, on relit le panier.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  const root = window.Shopify?.routes?.root ?? '/';
  fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) => {
      if (cart) setCartCount(cart.item_count);
    })
    .catch(() => {});
});

/** Expose la hauteur de l'en-tête custom aux composants d'Horizon qui la lisent. */
function observeHeaderHeight() {
  const header = document.querySelector('[data-tsb-header]');
  if (!(header instanceof HTMLElement) || typeof ResizeObserver === 'undefined') return;

  const update = () => {
    const height = header.offsetHeight;
    const inFlow = getComputedStyle(header).position !== 'absolute';
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${inFlow ? height : 0}px`);
  };

  new ResizeObserver(update).observe(header);
  update();
}

observeHeaderHeight();

/**
 * Horizon mémorise les tiroirs ouverts (sessionStorage « theme-drawer-open ») et les rouvre à la page suivante.
 * Pour le panier, le tiroir doit s'ouvrir une fois après un ajout, puis laisser la navigation tranquille :
 * l'attribut no-persist, prévu par Horizon, l'écarte de cette mémoire.
 */
function keepCartDrawerOutOfSession() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  drawer.setAttribute('no-persist', '');

  try {
    const key = 'theme-drawer-open';
    const saved = (sessionStorage.getItem(key) || '').split(',').filter(Boolean);
    if (!saved.includes(drawer.id)) return;
    const others = saved.filter((id) => id !== drawer.id);
    if (others.length) sessionStorage.setItem(key, others.join(','));
    else sessionStorage.removeItem(key);
  } catch {
    // Stockage de session indisponible : Horizon ne mémorise alors rien non plus.
  }
}

keepCartDrawerOutOfSession();
